import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { evolutionApi } from '../../services/evolutionApi';
import { supabase } from '../../services/supabase';
import Header from '../../components/Header';
import SenderPage from '../sender/SenderPage';
import '../../styles/components/dashboard.css';
import '../../styles/components/layout.css';

const DashboardPage = () => {
    const [activeTab, setActiveTab] = useState('instance');
    const [instanceName, setInstanceName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [instanceStatus, setInstanceStatus] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const { signOut, user } = useAuth();
    const navigate = useNavigate();

    // Função para formatar número de telefone
    const formatPhoneNumber = (value) => {
        // Remove tudo que não é número
        const numbers = value.replace(/\D/g, '');
        
        // Se não começar com 55, adiciona
        let formatted = numbers.startsWith('55') ? numbers : `55${numbers}`;
        
        // Se tiver DDD mas não tiver o 9, adiciona após o DDD
        if (formatted.length >= 4 && formatted.length <= 12) {
            const ddd = formatted.substring(2, 4);
            const rest = formatted.substring(4);
            if (!rest.startsWith('9')) {
                formatted = `55${ddd}9${rest}`;
            }
        }
        
        return formatted;
    };

    // Validar número de telefone
    const validatePhoneNumber = (number) => {
        const formatted = formatPhoneNumber(number);
        return formatted.length === 13;
    };

    // Carregar instância existente do usuário
    useEffect(() => {
        const loadUserInstance = async () => {
            try {
                const { data, error } = await supabase
                    .from('instances')
                    .select('*')
                    .eq('user_id', user.id)
                    .maybeSingle();

                if (error) throw error;

                if (data) {
                    setInstanceName(data.instance_name);
                    
                    // Verificar status real na Evolution API
                    try {
                        const realStatus = await handleCheckStatus(data.instance_name);
                        setInstanceStatus(realStatus);
                        
                        // Se o status no banco for diferente do real, atualizar
                        if (realStatus !== data.status) {
                            const { error: updateError } = await supabase
                                .from('instances')
                                .update({ status: realStatus })
                                .eq('user_id', user.id)
                                .eq('instance_name', data.instance_name);

                            if (updateError) {
                                console.error('Erro ao atualizar status no Supabase:', updateError);
                            }
                        }
                    } catch (statusError) {
                        console.error('Erro ao verificar status real:', statusError);
                        // Em caso de erro, usar o status do banco
                        setInstanceStatus(data.status);
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar instância:', error);
            }
        };

        if (user) {
            loadUserInstance();
        }
    }, [user]);

    // Verificar status periodicamente apenas durante a conexão inicial
    useEffect(() => {
        let intervalId;

        if (instanceName && instanceStatus === 'connecting') {
            intervalId = setInterval(() => {
                handleCheckStatus(instanceName);
            }, 10000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [instanceName, instanceStatus]);

    const handleLogout = async () => {
        try {
            await signOut();
            navigate('/');
        } catch (error) {
            console.error('Erro ao fazer logout:', error);
        }
    };

    const handleCreateInstance = async () => {
        if (!instanceName.trim()) {
            setError('Nome da instância é obrigatório');
            return;
        }

        if (!validatePhoneNumber(phoneNumber)) {
            setError('Número de telefone inválido. Formato esperado: 5521999999999');
            return;
        }

        setIsLoading(true);
        setError('');
        setQrCode(null);

        try {
            // Criar instância na Evolution API
            const formattedPhone = formatPhoneNumber(phoneNumber);
            const evolutionResponse = await evolutionApi.createInstance(instanceName, formattedPhone);
            console.log('Instância criada:', evolutionResponse);

            if (evolutionResponse.qrcode?.base64) {
                // Extrair apenas o código base64 da string data:image/png;base64,...
                const base64Data = evolutionResponse.qrcode.base64.split(',')[1];
                setQrCode(base64Data);
            }

            // Salvar no Supabase
            const { error: supabaseError } = await supabase
                .from('instances')
                .insert({
                    user_id: user.id,
                    instance_name: instanceName,
                    status: 'created',
                    evolution_instance_key: evolutionResponse.hash || null
                });

            if (supabaseError) {
                console.error('Erro ao salvar no Supabase:', supabaseError);
                throw new Error('Erro ao salvar instância no banco de dados');
            }

            setInstanceStatus('created');

            // Iniciar verificação periódica apenas para esconder o QR Code quando conectar
            const checkInterval = setInterval(async () => {
                const statusResponse = await handleCheckStatus(instanceName);
                if (statusResponse === 'connected') {
                    clearInterval(checkInterval);
                    setQrCode(null); // Esconde o QR Code quando conectar
                }
            }, 10000);

            // Limpar intervalo após 2 minutos
            setTimeout(() => clearInterval(checkInterval), 120000);

        } catch (error) {
            setError('Erro ao criar instância: ' + error.message);
            console.error('Erro:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDisconnect = async () => {
        try {
            // Chamar a API para desconectar
            const response = await evolutionApi.disconnectInstance(instanceName);
            console.log('Instância desconectada:', response);

            if (response.status === 'disconnected') {
                // Atualizar status no Supabase
                const { error: updateError } = await supabase
                    .from('instances')
                    .update({ status: 'disconnected' })
                    .eq('user_id', user.id)
                    .eq('instance_name', instanceName);

                if (updateError) {
                    console.error('Erro ao atualizar status no Supabase:', updateError);
                    throw updateError;
                }

                setInstanceStatus('disconnected');
                setQrCode(null);
            }
        } catch (error) {
            console.error('Erro ao desconectar:', error);
            setError('Erro ao desconectar instância');
        }
    };

    const handleConnect = async () => {
        try {
            setInstanceStatus('connecting');
            const response = await evolutionApi.getQrCodeForConnection(instanceName);
            console.log('QR Code obtido:', response);
            
            // Verifica se temos o QR code em qualquer um dos formatos possíveis
            if (response.qrcode?.base64 || response.base64) {
                // Usa o QR code de onde estiver disponível
                const base64String = response.qrcode?.base64 || response.base64;
                // Remove o prefixo data:image/png;base64, se existir
                const base64Data = base64String.includes('base64,') ? 
                    base64String.split('base64,')[1] : base64String;
                
                setQrCode(base64Data);
                
                // Atualizar status no Supabase
                const { error: updateError } = await supabase
                    .from('instances')
                    .update({ status: 'connecting' })
                    .eq('user_id', user.id)
                    .eq('instance_name', instanceName);

                if (updateError) {
                    console.error('Erro ao atualizar status no Supabase:', updateError);
                }

                // Iniciar verificação periódica
                const checkInterval = setInterval(async () => {
                    const statusResponse = await handleCheckStatus(instanceName);
                    if (statusResponse === 'connected') {
                        clearInterval(checkInterval);
                        setQrCode(null); // Esconde o QR Code quando conectar
                    }
                }, 10000);

                // Limpar intervalo após 2 minutos
                setTimeout(() => clearInterval(checkInterval), 120000);
            } else {
                throw new Error('QR Code não encontrado na resposta');
            }
        } catch (error) {
            console.error('Erro ao obter QR Code:', error);
            setError('Erro ao obter QR Code para conexão');
            setInstanceStatus('disconnected');
        }
    };

    const handleCheckStatus = async (name = instanceName) => {
        if (!name) return;

        try {
            const response = await evolutionApi.getConnectionState(name);
            console.log('Status da conexão:', response);
            
            // Se o status mudou, atualizar no Supabase
            if (response.status !== instanceStatus) {
                setInstanceStatus(response.status);

                const { error: updateError } = await supabase
                    .from('instances')
                    .update({ status: response.status })
                    .eq('user_id', user.id)
                    .eq('instance_name', name);

                if (updateError) {
                    console.error('Erro ao atualizar status no Supabase:', updateError);
                }
            }

            return response.status;
        } catch (error) {
            console.error('Erro ao verificar status:', error);
            return 'error';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'connected':
                return 'bg-green-500';
            case 'disconnected':
                return 'bg-red-500';
            case 'created':
                return 'bg-yellow-500';
            default:
                return 'bg-gray-500';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'connected':
                return 'Conectada';
            case 'disconnected':
                return 'Desconectada';
            case 'created':
                return 'Aguardando conexão';
            default:
                return 'Status desconhecido';
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="container mx-auto px-4 py-8">
                <nav className="flex gap-4 mb-8 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('instance')}
                        className={`pb-4 px-1 ${
                            activeTab === 'instance'
                                ? 'border-b-2 border-primary text-primary'
                                : 'text-gray-500 hover:text-primary'
                        }`}
                    >
                        Instância
                    </button>
                    <button
                        onClick={() => setActiveTab('sender')}
                        className={`pb-4 px-1 ${
                            activeTab === 'sender'
                                ? 'border-b-2 border-primary text-primary'
                                : 'text-gray-500 hover:text-primary'
                        }`}
                    >
                        Disparador
                    </button>
                </nav>

                {activeTab === 'instance' ? (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-6">
                            Gerenciar Instância
                        </h1>
                        
                        {/* Status da Instância */}
                        {instanceStatus && (
                            <div className="status-indicator mb-6">
                                <span className={`status-dot status-${instanceStatus}`}></span>
                                <span>{getStatusText(instanceStatus)}</span>
                            </div>
                        )}
                        
                        {/* QR Code */}
                        {qrCode && (
                            <div className="qr-code-container">
                                <img 
                                    src={`data:image/png;base64,${qrCode}`} 
                                    alt="QR Code para conexão" 
                                    className="mx-auto"
                                />
                                <p className="text-center text-sm text-gray-600 mt-2">
                                    Escaneie o QR Code no WhatsApp
                                </p>
                            </div>
                        )}
                        
                        {/* Formulário de Criação */}
                        <div className="space-y-6">
                            <div className="form-control">
                                <label htmlFor="instanceName">Nome da Instância</label>
                                <input
                                    type="text"
                                    id="instanceName"
                                    value={instanceName}
                                    onChange={(e) => setInstanceName(e.target.value)}
                                    disabled={instanceStatus !== null}
                                    placeholder="Digite o nome da instância"
                                />
                            </div>

                            {!instanceStatus && (
                                <div className="form-control">
                                    <label htmlFor="phoneNumber">Número do WhatsApp</label>
                                    <input
                                        type="text"
                                        id="phoneNumber"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="Ex: 5521999999999"
                                    />
                                    <p className="text-sm text-gray-500 mt-1">
                                        Digite o número no formato: 5521999999999 (55 + DDD + Número)
                                    </p>
                                </div>
                            )}

                            {/* Mensagens de Erro */}
                            {error && (
                                <div className="error-message">
                                    <p>{error}</p>
                                </div>
                            )}

                            {/* Botões de Ação */}
                            <div className="flex space-x-4">
                                {!instanceStatus && (
                                    <button
                                        onClick={handleCreateInstance}
                                        disabled={isLoading}
                                        className="action-button bg-primary text-white hover:bg-primary-dark"
                                    >
                                        {isLoading ? 'Criando...' : 'Criar Instância'}
                                    </button>
                                )}

                                {instanceStatus && (
                                    <>
                                        <button
                                            onClick={() => handleCheckStatus()}
                                            className="action-button bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        >
                                            Atualizar Status
                                        </button>

                                        {instanceStatus === 'connected' ? (
                                            <button
                                                onClick={handleDisconnect}
                                                className="action-button bg-error text-white hover:bg-red-700"
                                            >
                                                Desconectar
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleConnect}
                                                className="action-button bg-primary text-white hover:bg-primary-dark"
                                            >
                                                Conectar
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h1 className="text-2xl font-bold text-gray-900 mb-6">
                            Disparador de Mensagens
                        </h1>
                        {!instanceStatus || instanceStatus !== 'connected' ? (
                            <div className="text-center p-8">
                                <p className="text-gray-600 mb-4">
                                    Para usar o disparador, você precisa primeiro conectar sua instância do WhatsApp.
                                </p>
                                <button
                                    onClick={() => setActiveTab('instance')}
                                    className="text-accent hover:text-accent-dark font-medium"
                                >
                                    Ir para Configuração da Instância
                                </button>
                            </div>
                        ) : (
                            <SenderPage />
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default DashboardPage;
